import { faFileSignature, faPaperPlane, faSignature } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UnsignedAgreement } from '@spike/order-model';
import { fetchSignaturesByClientThunk, resendThunk } from 'actions/agreements/AgreementsActions';
import AgreementSignature from 'components/Agreements/AgreementsSignature';
import Dropdown from 'components/Agreements/ui/Dropdown';
import { DropdownOption } from 'components/Agreements/ui/Dropdown/model';
import { OverFullWindow } from 'components/UI';
import { Signature } from 'model/Agreements';
import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AgreementsStatus } from 'reducers/agreements/AgreementsState';
import { RootState } from 'store';
import styles from './UnsignedAgreementAlert.module.scss';

interface UnsignedAgreementAlertProps {
    agreements: UnsignedAgreement[];
    clientId: number;
    onSigned: () => void;
}

const signNowOption: DropdownOption = {
    id: 'sign_now',
    title: 'Sign Now',
    icon: faSignature
};

const resendOption: DropdownOption = {
    id: 'resend',
    title: 'Resend',
    icon: faPaperPlane
};

const options = [signNowOption, resendOption];

export const UnsignedAgreementAlert: FunctionComponent<UnsignedAgreementAlertProps> = props => {
    const dispatch = useDispatch();
    const agreementsStatus = useSelector<RootState, AgreementsStatus>(state => state.agreements.status);
    const signatures = useSelector<RootState, Signature[]>(state => state.agreements.signaturesByClient);

    const [pendingSignatureId, setPendingSignatureId] = useState<number>();
    const [signatureToSign, setSignatureToSign] = useState<Signature>();
    const [refreshAfterClose, setRefreshAfterClose] = useState(false);

    const openSignature = (signatureId: number) => {
        const signature = signatures.find(item => item.id === signatureId);

        if (signature) {
            setSignatureToSign(signature);
            return;
        }

        setPendingSignatureId(signatureId);
        dispatch(fetchSignaturesByClientThunk(props.clientId));
    };

    useEffect(() => {
        if (agreementsStatus === AgreementsStatus.FetchSignaturesByClientSuccess && pendingSignatureId !== undefined) {
            const signature = signatures.find(item => item.id === pendingSignatureId);
            setPendingSignatureId(undefined);

            if (signature) {
                setSignatureToSign(signature);
            }
        }

        if (agreementsStatus === AgreementsStatus.SignSuccess && signatureToSign) {
            setRefreshAfterClose(true);
        }

        if (agreementsStatus === AgreementsStatus.Error) {
            setPendingSignatureId(undefined);
        }
    }, [agreementsStatus, pendingSignatureId, signatureToSign, signatures]);

    const selectOption = (agreement: UnsignedAgreement, optionId: string) => {
        if (optionId === signNowOption.id) {
            openSignature(agreement.signatureId);
        }

        if (optionId === resendOption.id) {
            dispatch(resendThunk(agreement.signatureId));
        }
    };

    const closeSignature = () => {
        setSignatureToSign(undefined);

        if (refreshAfterClose) {
            setRefreshAfterClose(false);
            props.onSigned();
        }
    };

    const agreements = props.agreements.filter(agreement => Boolean(agreement.signatureId));
    const multiple = agreements.length > 1;

    if (agreements.length === 0) {
        return null;
    }

    return (
        <>
            <div className={multiple ? styles.multipleAlert : styles.alert}>
                <div className={styles.content}>
                    {multiple ? (
                        <>
                            <div className={styles.titleRow}>
                                <FontAwesomeIcon
                                    icon={faFileSignature}
                                    className={styles.icon}
                                />
                                <div className={styles.title}>Agreements Pending Signature</div>
                            </div>
                            <div className={styles.agreementList}>
                                {agreements.map(agreement => (
                                    <div
                                        className={styles.agreementRow}
                                        key={agreement.signatureId}
                                    >
                                        <span className={styles.agreementName}>{agreement.name}</span>
                                        <div className={styles.rowMenu}>
                                            <Dropdown
                                                options={options}
                                                onClick={optionId => selectOption(agreement, optionId)}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className={styles.titleRow}>
                                <FontAwesomeIcon
                                    icon={faFileSignature}
                                    className={styles.icon}
                                />
                                <div className={styles.title}>{agreements[0].name}</div>
                            </div>
                            <div className={styles.description}>Client hasn’t signed the agreement.</div>
                        </>
                    )}
                </div>
                {!multiple && (
                    <Dropdown
                        options={options}
                        onClick={optionId => selectOption(agreements[0], optionId)}
                    />
                )}
            </div>
            {signatureToSign && (
                <OverFullWindow onClose={closeSignature}>
                    <AgreementSignature
                        hash={signatureToSign.secureHash}
                        onClose={closeSignature}
                    />
                </OverFullWindow>
            )}
        </>
    );
};

export default UnsignedAgreementAlert;
